import type {
  GroupLifeCalculateAnalysisPayload,
  GroupLifeCalculateAnalysisResult,
  GroupLifeMemberCrudResponse,
  GroupLifeQuoteDetails,
  GroupLifeCalculateProposalPayload,
} from '@/services/group-life';
import { BaseService } from '../../base';

import type { ProposalSignatureMethodResponse } from '../types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

export type GroupLifeProposalStepName =
  | 'insurance'
  | 'plan'
  | 'payment'
  | 'resume';

export type GroupLifeProposalSaveStepResponse = {
  policyId: string;
  step: string;
  savedAt: string;
};

export type GroupLifeProposalVersionItem = {
  quotationVersionId: number;
  versionName: string;
  versionDescription?: string;
  isSelected?: string;
  type: string;
  date: string;
  number: string;
  lives: number;
  removed: number;
  averageRate: number;
  totalPremium: number;
  /**
   * Procedência da versão numa proposta clonada: CURRENT = da própria
   * proposta; SOURCE = herdada da proposta de origem (cadeia de clonagem).
   */
  origin?: 'SOURCE' | 'CURRENT';
  /** PolicyId da proposta de origem dona da versão (apenas itens SOURCE). */
  sourcePolicyId?: string;
};

export type GroupLifeCloneProposalResponse = {
  policyId: string;
  proposalNumber: string;
  productCode: string;
  /** O recálculo das vidas segue depois do clone. */
  recalculationPending?: boolean;
  /** Aviso enquanto o recálculo não termina. */
  warning?: string;
};

export type ListGroupLifeProposalVersionsResponse = {
  policyId: string;
  versions: GroupLifeProposalVersionItem[];
};

export type { GroupLifeCalculateAnalysisResult } from '@/services/group-life';

export type GroupLifeProposalPdfResponse = {
  url: string;
  key: string;
};

export type GroupLifeCommercialLetterResponse = {
  url: string;
  key: string;
};

export type GroupLifeSignedCommercialLetterResponse = {
  url: string;
  key: string;
  fileName: string;
};

/**
 * Resposta do Confirmar do Resumo: leva a proposta para "Em assinatura" (12)
 * ou, quando houve alteração desde a última confirmação, para a subscrição
 * (18) — é o Confirmar que roda o portão de subscrição.
 */
export type GroupLifeConfirmProposalResponse = {
  policyId: string;
  statusPortal: string;
  /** true quando a proposta foi para a análise e NÃO para a assinatura. */
  sentToUnderwritingAnalysis?: boolean;
  /** Critérios que puxaram a proposta para a análise. */
  underwritingMessages?: Record<string, unknown>[];
};

export type GroupLifeSubmitProposalAnalysisResponse = {
  policyId: string;
  statusPortal: string;
  proposalNo?: string;
};

export type GroupLifeRejectProposalAnalysisResponse = {
  policyId: string;
  statusPortal: string;
  description: string;
  refusalLetterGenerated: boolean;
  refusalLetterKey?: string;
};

export type GroupLifeCommercialLetterProposal = {
  Branch: string;
  Affiliate: string;
  Number: string;
  ValidUntil: string;
  PolicyStart: string;
  PolicyEnd: string;
  PolicyTypeLine: string;
  CommissionRate: string;
  BrokerageRate: string;
};

export type GroupLifeCommercialLetterBroker = {
  Name: string;
  SusepCode: string;
  Document?: string;
  Participation?: string;
};

export type GroupLifeCommercialLetterCoBroker = {
  Name: string;
  CNPJ: string;
  SusepCode: string;
  Participation: string;
};

export type GroupLifeCommercialLetterIntermediaryBroker = {
  Name: string;
  SusepCode: string;
  Commission: string;
  BrokerageRate: string;
};

export type GroupLifeCommercialLetterStipulant = {
  CompanyName: string;
  CNPJ: string;
};

export type GroupLifeCommercialLetterPayload = {
  proposal: GroupLifeCommercialLetterProposal;
  broker: GroupLifeCommercialLetterBroker;
  intermediaryBroker: GroupLifeCommercialLetterIntermediaryBroker;
  stipulant: GroupLifeCommercialLetterStipulant;
  coBrokers?: GroupLifeCommercialLetterCoBroker[];
};

/**
 * O PDF da PROPOSTA é montado pelo back-end a partir do policyId — o initiate
 * só carrega o mapping da carta comercial (migração pendente).
 */
export type InitiatePmeDocusignSignaturesPayload = {
  commercialLetter: GroupLifeCommercialLetterPayload;
};

export class GroupLifeProposalsService extends BaseService {
  async initiateProposal(policyId: string): Promise<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: string;
    status?: number;
  }> {
    try {
      const path = '/portal/v1/group-life/proposals';
      const response = await this.post(path, { policyId }, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<Record<string, unknown>>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao iniciar proposta',
      };
    }
  }

  async getProposalDetails(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeQuoteDetails;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}`;
      // `no-store`: o snapshot é relido logo após o recálculo (confirmação da
      // persistência), então não pode vir do cache do browser.
      const response = await this.get(
        path,
        { cache: 'no-store' },
        PORTAL_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeQuoteDetails>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao carregar proposta',
      };
    }
  }

  /**
   * Cancela a proposta PME e clona uma nova em digitação. A resposta volta
   * na hora; `warning` avisa que o recálculo das vidas ainda está em execução.
   */
  async alterProposal(
    policyId: string,
    idempotencyKey?: string
  ): Promise<{
    success: boolean;
    data?: GroupLifeCloneProposalResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/alter`;
      const response = await this.post(
        path,
        {},
        idempotencyKey
          ? { headers: { 'Idempotency-Key': idempotencyKey } }
          : {},
        PORTAL_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeCloneProposalResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao alterar proposta',
      };
    }
  }

  /**
   * @param idempotencyKey repetida em toda tentativa da mesma clonagem: a API
   * devolve o clone já criado (ou 409 enquanto ele roda) em vez de criar outro.
   */
  async cloneProposal(
    policyId: string,
    idempotencyKey?: string
  ): Promise<{
    success: boolean;
    data?: GroupLifeCloneProposalResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/clone`;
      const response = await this.post(
        path,
        {},
        idempotencyKey
          ? { headers: { 'Idempotency-Key': idempotencyKey } }
          : {},
        PORTAL_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeCloneProposalResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao clonar proposta',
      };
    }
  }

  async listProposalVersions(policyId: string): Promise<{
    success: boolean;
    data?: ListGroupLifeProposalVersionsResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/versions`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<ListGroupLifeProposalVersionsResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao listar versões da proposta',
      };
    }
  }

  async getProposalAtVersion(
    policyId: string,
    quotationVersionId: string | number
  ): Promise<{
    success: boolean;
    data?: GroupLifeQuoteDetails;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/versions/${encodeURIComponent(String(quotationVersionId))}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeQuoteDetails>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar a versão da proposta',
      };
    }
  }

  async listMembersByTarget(params: {
    policyId: string;
    targetId: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMemberCrudResponse[];
    error?: string;
    status?: number;
  }> {
    try {
      const search = new URLSearchParams({ targetId: params.targetId });
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        params.policyId
      )}/members?${search.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeMemberCrudResponse[]>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  async saveStep(params: {
    policyId: string;
    step: GroupLifeProposalStepName;
    payload: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    data?: GroupLifeProposalSaveStepResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        params.policyId
      )}/step/${encodeURIComponent(params.step)}`;
      const response = await this.fetchWithAuth(
        path,
        {
          method: 'PATCH',
          body: JSON.stringify({ payload: params.payload }),
        },
        PORTAL_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeProposalSaveStepResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao salvar o step',
      };
    }
  }

  async calculateProposal(
    policyId: string,
    payload?: GroupLifeCalculateProposalPayload
  ): Promise<{
    success: boolean;
    data?: { sentToUnderwritingAnalysis?: boolean };
    error?: string;
    status?: number;
    sentToUnderwritingAnalysis?: boolean;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/calculate`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      const result = await this.handleJsonResponse<{
        sentToUnderwritingAnalysis?: boolean;
      }>(response);
      return {
        ...result,
        status: response.status,
        sentToUnderwritingAnalysis:
          result.data?.sentToUnderwritingAnalysis === true,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao calcular proposta',
      };
    }
  }

  async calculateProposalAnalysis(
    policyId: string,
    payload?: GroupLifeCalculateAnalysisPayload
  ): Promise<{
    success: boolean;
    data?: GroupLifeCalculateAnalysisResult;
    error?: string;
    status?: number;
    sentToUnderwritingAnalysis?: boolean;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/calculate-analysis`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeCalculateAnalysisResult>(
          response
        );
      return {
        ...result,
        status: response.status,
        sentToUnderwritingAnalysis:
          result.data?.sentToUnderwritingAnalysis === true,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao calcular proposta',
      };
    }
  }

  async transmitProposal(policyId: string): Promise<{
    success: boolean;
    data?: unknown;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/transmit`;
      const response = await this.post(path, undefined, PORTAL_API_URL);
      const result = await this.handleJsonResponse<unknown>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao transmitir proposta',
      };
    }
  }

  async confirmProposal(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeConfirmProposalResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/confirm`;
      const response = await this.post(path, undefined, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeConfirmProposalResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao confirmar a proposta',
      };
    }
  }

  async submitProposalAnalysisCalculation(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeSubmitProposalAnalysisResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/submit-analysis-calculation`;
      const response = await this.post(path, undefined, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeSubmitProposalAnalysisResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao aprovar proposta em análise',
      };
    }
  }

  async rejectProposalAnalysis(
    policyId: string,
    description: string
  ): Promise<{
    success: boolean;
    data?: GroupLifeRejectProposalAnalysisResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/reject-analysis`;
      const response = await this.post(path, { description }, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeRejectProposalAnalysisResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao recusar proposta em análise',
      };
    }
  }

  /**
   * Gera o PDF da proposta. O mapping do template é montado pelo back-end a
   * partir do policyId — o front não envia nenhum dado do documento.
   */
  async generatePdf(params: { policyId: string }): Promise<{
    success: boolean;
    data?: GroupLifeProposalPdfResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        params.policyId
      )}/pdf`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeProposalPdfResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao gerar PDF',
      };
    }
  }

  async commercialLetter(params: {
    policyId: string;
    payload: GroupLifeCommercialLetterPayload;
  }): Promise<{
    success: boolean;
    data?: GroupLifeCommercialLetterResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        params.policyId
      )}/commercial-letter`;
      const response = await this.post(path, params.payload, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeCommercialLetterResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar a carta comercial',
      };
    }
  }

  /**
   * Link da carta comercial ASSINADA via DocuSign. Não gera o documento: a API
   * responde 404 enquanto a assinatura não foi concluída.
   */
  async getSignedCommercialLetter(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeSignedCommercialLetterResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/commercial-letter/signed`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeSignedCommercialLetterResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao baixar a carta comercial assinada',
      };
    }
  }

  async getSignatureMethod(policyId: string): Promise<{
    success: boolean;
    data?: ProposalSignatureMethodResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/signatures/method`;
      const response = await this.get(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await this.extractError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<ProposalSignatureMethodResponse>(
          response
        );
      return { ...parsed, statusCode: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar o método de assinatura',
      };
    }
  }

  async initiateDocusignSignatures(params: {
    policyId: string;
    payload: InitiatePmeDocusignSignaturesPayload;
  }): Promise<{
    success: boolean;
    data?: import('../types').PmeDocusignSignaturesResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        params.policyId
      )}/signatures/docusign/initiate`;
      const response = await this.post(path, params.payload, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await this.extractError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<
          import('../types').PmeDocusignSignaturesResponse
        >(response);
      return {
        ...parsed,
        statusCode: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao iniciar assinaturas digitais',
      };
    }
  }

  async getDocusignSignaturesStatus(policyId: string): Promise<{
    success: boolean;
    data?: import('../types').PmeDocusignSignaturesResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/signatures/docusign`;
      const response = await this.get(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await this.extractError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<
          import('../types').PmeDocusignSignaturesResponse
        >(response);
      return {
        ...parsed,
        statusCode: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar assinaturas digitais',
      };
    }
  }

  async syncDocusignSignatures(
    policyId: string,
    kind?: string
  ): Promise<{
    success: boolean;
    data?: import('../types').PmeDocusignSignaturesResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const query = kind ? `?kind=${encodeURIComponent(kind)}` : '';
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/signatures/docusign/sync${query}`;
      const response = await this.post(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await this.extractError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<
          import('../types').PmeDocusignSignaturesResponse
        >(response);
      return {
        ...parsed,
        statusCode: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao atualizar status',
      };
    }
  }

  async retryDocusignSignature(
    policyId: string,
    envelopeKind: string
  ): Promise<{
    success: boolean;
    data?: import('../types').PmeDocusignSignaturesResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/proposals/${encodeURIComponent(
        policyId
      )}/signatures/docusign/retry`;
      const response = await this.post(path, { envelopeKind }, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await this.extractError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<
          import('../types').PmeDocusignSignaturesResponse
        >(response);
      return {
        ...parsed,
        statusCode: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao retentar assinatura',
      };
    }
  }

  private async extractError(
    response: Response
  ): Promise<{ success: false; error: string }> {
    const text = (await response.text()).trim();
    if (!text) {
      return {
        success: false,
        error: `Erro ${response.status}: ${response.statusText}`,
      };
    }

    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      const message =
        (typeof data.detail === 'string' && data.detail) ||
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error) ||
        (typeof data.title === 'string' && data.title);
      // Assinaturas não passam pelo `handleJsonResponse`: avisa aqui.
      this.notifyInactiveBroker(response.status, message);
      return {
        success: false,
        error: message || `Erro ${response.status}: ${response.statusText}`,
      };
    } catch {
      return { success: false, error: text };
    }
  }
}

export const groupLifeProposalsService = new GroupLifeProposalsService();
